import { z } from 'zod';
import { QAQC_METRIC_CATEGORIES } from '../calc/qaqc.js';
/**
 * App 1 contracts — imported by both the entry form and the route (ADR-0010), so
 * a front-end/back-end validation mismatch is a compile error rather than a
 * production surprise.
 *
 * Two tiers per entity, and the split is the important part:
 *
 *   **Draft** — types and ranges only. A half-entered form must be savable; a
 *   user interrupted mid-entry should not lose their work to a validation rule
 *   about a field they have not reached yet.
 *
 *   **Submit** — the draft rules plus the cross-field ones. The material
 *   inspection breakdown must reconcile, the brief must carry an AI disposition.
 *   These bite at submit, which is where the record stops being scratch and
 *   becomes evidence.
 */
const count = z.number().int().nonnegative();
/**
 * A reporting period is identified by the first day of its month.
 *
 * ASSUMPTION, flagged for Algihaz (Rule 6): calendar months, no fiscal-year
 * offset. If Algihaz report on a fiscal calendar, period identity changes
 * everywhere and this is the place it starts.
 */
export const periodStartSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
    .refine((value) => value.endsWith('-01'), 'A reporting period starts on the first of the month.');
export const qaqcMetricCategorySchema = z.enum(QAQC_METRIC_CATEGORIES);
// ---------------------------------------------------------------------------
// Report period (the monthly package)
// ---------------------------------------------------------------------------
export const reportPeriodCreateSchema = z.object({
    projectId: z.string().uuid(),
    periodStart: periodStartSchema,
});
export const reportPeriodSendBackSchema = z.object({
    // Mandatory, and long enough to be a reason rather than a shrug. "Sent back" is
    // a real state carrying the approver's comments — the API enforces this, not
    // just the form.
    remarks: z.string().trim().min(10, 'Explain what needs changing, in at least 10 characters.'),
});
// ---------------------------------------------------------------------------
// QAQC metrics
// ---------------------------------------------------------------------------
/**
 * Note what is NOT here: `closureRate` and `closureRateVariance`. Derived values
 * are recomputed server-side and never accepted from a payload (ADR-0007), so the
 * schema has no field for a client to send one through.
 *
 * There is also deliberately no `closed <= issued` rule. Real monthly data
 * produces it — items issued in an earlier period get closed in this one — and
 * `closureRate` already has a defined answer for it.
 */
export const qaqcMetricDraftSchema = z.object({
    category: qaqcMetricCategorySchema,
    issuedCount: count,
    closedCount: count,
    ageing0To15: count,
    ageing15To45: count,
    ageingOver45: count,
});
export const qaqcMetricSubmitSchema = qaqcMetricDraftSchema;
// ---------------------------------------------------------------------------
// Material inspection
// ---------------------------------------------------------------------------
export const materialInspectionDraftSchema = z.object({
    mirnTotal: count,
    osdOverage: count,
    osdShortage: count,
    osdDamage: count,
    statusApproved: count,
    statusOnHold: count,
    statusRejected: count,
    statusHazardous: count,
    statusHandleWithCare: count,
});
/**
 * Spec §9: the status breakdown must reconcile to the entered total.
 *
 * ASSUMPTION, flagged (Rule 6): OSD counts are a separate dimension and are not
 * part of this total. Including them would reject valid data.
 */
export const materialInspectionSubmitSchema = materialInspectionDraftSchema.superRefine((value, ctx) => {
    const statusTotal = value.statusApproved +
        value.statusOnHold +
        value.statusRejected +
        value.statusHazardous +
        value.statusHandleWithCare;
    if (statusTotal !== value.mirnTotal) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mirnTotal'],
            message: `The status breakdown totals ${statusTotal}, which does not match the MIRN total of ${value.mirnTotal}.`,
        });
    }
});
// ---------------------------------------------------------------------------
// Quality Toolbox Talks
// ---------------------------------------------------------------------------
export const qtbtDraftSchema = z.object({
    sessionCount: count,
    attendeeCount: count,
    totalDurationMinutes: count,
});
export const qtbtSubmitSchema = qtbtDraftSchema.superRefine((value, ctx) => {
    // Attendance without a session is a data-entry slip, not a valid figure.
    if (value.sessionCount === 0 && value.attendeeCount > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sessionCount'],
            message: 'Attendees were recorded but no sessions were held.',
        });
    }
});
// ---------------------------------------------------------------------------
// Customer satisfaction
// ---------------------------------------------------------------------------
const rating = z.number().int().min(1).max(5).nullable();
export const customerSatisfactionSchema = z.object({
    respondentName: z.string().trim().max(200).nullable().optional(),
    ratingQuality: rating.optional(),
    ratingTimeliness: rating.optional(),
    ratingCommunication: rating.optional(),
    ratingResponsiveness: rating.optional(),
    ratingDocumentation: rating.optional(),
    ratingOverall: rating.optional(),
    /** Yes / No / Partially outcome questions, keyed by question code. */
    outcomeAnswers: z.record(z.enum(['yes', 'no', 'partially'])).default({}),
    feedback: z.string().trim().max(5000).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Daily Document Governance Log
// ---------------------------------------------------------------------------
export const documentGovernanceDraftSchema = z.object({
    projectId: z.string().uuid(),
    disciplineId: z.string().uuid().nullable().optional(),
    logDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.'),
    submittalsTotal: count,
    drawingsTotal: count,
    statusApproved: count,
    statusResubmit: count,
    statusRejected: count,
    statusUnderReview: count,
    /** Inputs for the derived average — see ADR-0007; the average itself is not accepted. */
    reviewDaysTotal: count,
    reviewedCount: count,
    pendingDaysByEntity: z
        .object({
        client: z.number().nonnegative().optional(),
        algihaz: z.number().nonnegative().optional(),
        supplier: z.number().nonnegative().optional(),
    })
        .default({}),
    correspondenceIncoming: count,
    correspondenceOutgoing: count,
});
export const documentGovernanceSubmitSchema = documentGovernanceDraftSchema.superRefine((value, ctx) => {
    const statusTotal = value.statusApproved + value.statusResubmit + value.statusRejected + value.statusUnderReview;
    const documentTotal = value.submittalsTotal + value.drawingsTotal;
    if (statusTotal !== documentTotal) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['statusApproved'],
            message: `The status breakdown totals ${statusTotal}, which does not match the ${documentTotal} submittals and drawings recorded.`,
        });
    }
    if (value.reviewedCount === 0 && value.reviewDaysTotal > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reviewedCount'],
            message: 'Review days were recorded but no reviews were completed.',
        });
    }
});
// ---------------------------------------------------------------------------
// Quality Assessment Brief — the human-in-the-loop contract (ADR-0008)
// ---------------------------------------------------------------------------
export const aiDispositionSchema = z.enum(['accepted', 'edited', 'rejected', 'not_used']);
/**
 * ADR-0008 in the type system: naming an AI suggestion obliges you to say what you
 * did with it. The API checks the claim against the text as well — a client that
 * reports 'accepted' while sending altered text is rejected there, because a
 * schema cannot see the suggestion it is claiming to have accepted.
 */
export const qualityBriefSaveSchema = z
    .object({
    narrative: z.string().trim().max(20000),
    aiSuggestionId: z.string().uuid().nullable().optional(),
    aiDisposition: aiDispositionSchema.nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.aiSuggestionId && (!value.aiDisposition || value.aiDisposition === 'not_used')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['aiDisposition'],
            message: 'An AI suggestion was referenced, so record whether it was accepted, edited or rejected.',
        });
    }
    if (!value.aiSuggestionId && value.aiDisposition && value.aiDisposition !== 'not_used') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['aiSuggestionId'],
            message: 'A disposition was recorded without an AI suggestion to apply it to.',
        });
    }
});
// ---------------------------------------------------------------------------
// PQI weighting (master data, Rule 1)
// ---------------------------------------------------------------------------
export const pqiWeightSetSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    weights: z.object({
        external_ncr: z.number().min(0).max(1),
        internal_ncr: z.number().min(0).max(1),
        rfi: z.number().min(0).max(1),
        rmi: z.number().min(0).max(1),
    }),
    ageingPenaltyPointsPerPct: z.number().min(0).max(100),
    ageingPenaltyCap: z.number().min(0).max(100),
    isProvisional: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    const total = Object.values(value.weights).reduce((sum, weight) => sum + weight, 0);
    // The invariant that makes this one atomic object rather than four settings.
    if (Math.abs(total - 1) > 1e-6) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['weights'],
            message: `The four weights must sum to 1. They currently sum to ${total.toFixed(4)}.`,
        });
    }
});
// ---------------------------------------------------------------------------
// Dashboard and export filters (spec §9 key screens)
// ---------------------------------------------------------------------------
export const qaqcDashboardQuerySchema = z.object({
    projectGroup: z.string().trim().optional(),
    projectId: z.string().uuid().optional(),
    businessUnitId: z.string().uuid().optional(),
    fromPeriod: periodStartSchema.optional(),
    toPeriod: periodStartSchema.optional(),
    category: qaqcMetricCategorySchema.optional(),
});
//# sourceMappingURL=qaqc.js.map